import {
  Typography,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Box,
} from "@mui/material";
import { decryptData } from "../../../crypto";
import Diler from "./Diler";
import { useRef, useState, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Draggable from "react-draggable";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export const QuestionRenderer = ({
  question,
  selectedLanguage,
  questionType,
  selectedOptionId,
  selectedOptionIds,
  inputAnswer,
  handleOptionChange,
  handleMultipleChoiceChange,
  handleInputChange,
  setMatrixAnswer,
  matrixAnswer,
  classes,
  text,
  setText,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState("");
  const [optionClick, setOptionClick] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [touchStartDist, setTouchStartDist] = useState(null);
  const draggableRef = useRef(null);
  //  start  Only for the Zoom 
  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.5, 4));
  const handleZoomOut = () =>
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Pinch-to-zoom logic
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      setTouchStartDist(getDistance(e.touches));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDist) {
      const dist = getDistance(e.touches);
      const ratio = dist / touchStartDist;
      setScale((prev) => Math.min(Math.max(prev * ratio, 1), 4));
      setTouchStartDist(dist);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartDist(null);
  };

  // Mouse wheel zoom logic
  const handleWheel = (e) => {
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  // Double tap/click to toggle zoom
  const handleDoubleClick = () => {
    if (scale > 1) {
      handleResetZoom();
    } else {
      setScale(2);
    }
  };
  //   end  Only for the Zoom 
  const modules = useMemo(
    () => ({
      toolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ script: "sub" }, { script: "super" }],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "code-block"],
        ["clean"], // Added a clear formatting button
      ],
    }),
    [],
  );

  const handleImageClick = (e) => {
    const src = e.target.src;
    if (src) {
      setSelectedImg(src);
      setOpen(true);
    }
  };

  function sortAlphabetArray(arr) {
    return [...new Set(arr.map((a) => a.toString()))].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }

  const handleOptionImageClick = (e) => {
    const src = e.target.src;
    if (src) {
      setOptionClick(true);
      setSelectedImg(src);
      setOpen(true);
    }
  };

  const inputRef = useRef(null);
  const kbVal = sessionStorage.getItem("keyboard_enable");
  const isKeyboardEnabled =
    kbVal === true || kbVal === "true" || kbVal === "yes" || kbVal === 1 || kbVal === "1";

  const renderOptions = () => {
    const x = question.options.flatMap((option) =>
      option?.solution_en?.split(",").map((o) => o.trim()),
    );

    switch (questionType) {
      case "Single":
        return (
          <ul className={classes.options}>
            {question.options.map((option, index) => (
              <li
                key={index}
                style={{
                  display: "list-item",
                }}
                onClick={(e) => {
                  // Detect if user clicked an image inside HTML
                  if (e.target.tagName === "IMG") {
                    handleOptionImageClick(e);
                  }
                }}
              >
                <div style={{ display: "inline-flex", alignItems: "flex-start" }}>
                  <input
                    type="radio"
                    value={option.id}
                    style={{ backgroundColor: "red", marginTop: "4px" }}
                    checked={Number(selectedOptionId) === option.id}
                    onChange={handleOptionChange}
                  />
                  <label
                    style={{ display: "inline-flex", alignItems: "flex-start" }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            selectedLanguage === "english"
                              ? option.option_en
                              : option.option_hi,
                        }}
                      />
                    </div>
                  </label>
                </div>
              </li>
            ))}
          </ul>
        );

      case "Multiple Choice":
        return (
          <ul className={classes.options}>
            {question.options.map((option, index) => (
              <li
                key={index}
                style={{
                  display: "list-item",
                }}
                onClick={(e) => {
                  // Detect if user clicked an image inside HTML
                  if (e.target.tagName === "IMG") {
                    handleOptionImageClick(e);
                  }
                }}
              >
                <FormControlLabel
                  style={{ display: "inline-flex", alignItems: "flex-start" }}
                  control={
                    <Checkbox
                      style={{ padding: "0 9px", marginTop: "2px" }}
                      checked={selectedOptionIds.includes(option.id)}
                      onChange={(e) => handleMultipleChoiceChange(e, option.id)}
                      value={option.id}
                    />
                  }
                  label={
                    <div
                      style={{ display: "flex", alignItems: "flex-start" }}
                      onClick={(e) => e.preventDefault()}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            selectedLanguage === "english"
                              ? option.option_en
                              : option.option_hi,
                        }}
                      />
                    </div>
                  }
                />
              </li>
            ))}
          </ul>
        );
      case "Range":
        return (
          <>
            <input
              ref={inputRef}
              type="text"
              inputMode={isKeyboardEnabled ? "text" : "none"}
              className={`${classes.dflex} ${classes.flexdirectioncolumn} ${classes.numberInput}`}
              style={{
                marginBottom: "15px",
                width: "120px",
                outline: "none",
              }}
              value={inputAnswer}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only digits, optional minus sign at start, and at most one dot
                if (/^-?\d*\.?\d*$/.test(value)) {
                  handleInputChange(e);
                }
              }}
              placeholder="Enter a number"
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (!isKeyboardEnabled) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                if (!isKeyboardEnabled) {
                  e.preventDefault();
                }
              }}
            />

            <Diler
              handleInputChange={handleInputChange}
              inputAnswer={inputAnswer}
              inputRef={inputRef}
            />
          </>
        );
      case "Fill In The Blanks":
        return (
          <>
            <input
              ref={inputRef}
              type="text"
              inputMode={isKeyboardEnabled ? "text" : "none"}
              className={`${classes.dflex} ${classes.flexdirectioncolumn} ${classes.numberInput}`}
              style={{
                marginBottom: "15px",
                width: "120px",
                outline: "none",
              }}
              value={inputAnswer}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only digits, optional minus sign at start, and at most one dot
                if (/^-?\d*\.?\d*$/.test(value)) {
                  handleInputChange(e);
                }
              }}
              placeholder="Enter a number"
              onWheel={(e) => e.target.blur()}
              onKeyDown={(e) => {
                if (!isKeyboardEnabled) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                if (!isKeyboardEnabled) {
                  e.preventDefault();
                }
              }}
            />

            <Diler
              handleInputChange={handleInputChange}
              inputAnswer={inputAnswer}
              inputRef={inputRef}
            />
          </>
        );
      case "Essay Writing":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: { xs: 400, md: 450 },
              mx: { xs: 1, sm: 2, md: 3 }, // Wider on all screens
              mt: 2,
              mb: 12, // Extra bottom margin for better scrolling experience
              borderRadius: "8px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "1px solid #d1d1d1",
              "& .ql-toolbar.ql-snow": {
                border: "none",
                borderBottom: "1px solid #d1d1d1",
                backgroundColor: "#fcfcfc",
                display: "flex",
                flexWrap: "wrap",
                padding: "8px",
              },
              "& .ql-container.ql-snow": {
                border: "none",
                flexGrow: 1,
                fontSize: "16px",
                fontFamily: "'Inter', sans-serif",
                backgroundColor: "#fff",
              },
              "& .ql-editor": {
                minHeight: "100%",
                lineHeight: 1.6,
              },
              "& .ql-editor.ql-blank::before": {
                fontStyle: "italic",
                color: "#999",
              }
            }}
          >
            <ReactQuill
              theme="snow"
              style={{ height: "100%", display: "flex", flexDirection: "column" }}
              value={text}
              onChange={setText}
              placeholder="Start writing your essay here..."
              modules={modules}
            />
          </Box>
        );

      case "Match":
        return (
          <Box sx={{ pb: 10 }}>
            <div
              className={classes.options}
              style={{
                display: "flex",
                paddingLeft: "55px",
                gap: "50px",
                marginTop: 30,
              }}
            >
              {sortAlphabetArray(x).map((option, index) => (
                <div key={index}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: selectedLanguage === "english" ? option : option,
                    }}
                  />
                </div>
              ))}
            </div>
            <ul className={classes.options}>
              {question.options.map((option, index) => {
                if (
                  (!option.option_en && !option.option_hi) ||
                  (!option.solution_en && !option.solution_hi)
                ) {
                  return;
                }

                return (
                  <li
                    key={index}
                    style={{ display: "list-item", listStyle: "none" }}
                    onClick={(e) => {
                      // Detect if user clicked an image inside HTML
                      if (e.target.tagName === "IMG") {
                        handleOptionImageClick(e);
                      }
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center" }}
                      onClick={(e) => e.preventDefault()}
                    >
                      {" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            selectedLanguage === "english"
                              ? option.option_en
                              : option.option_hi,
                        }}
                      />
                      <div style={{ marginLeft: "20px" }}></div>
                      {sortAlphabetArray(x).map((alpha) => {
                        const rowKey =
                          selectedLanguage === "english"
                            ? option.option_en
                            : option.option_hi;

                        const selectedArr = matrixAnswer?.[rowKey] || [];

                        return (
                          <input
                            key={alpha}
                            type="radio"
                            name={rowKey} // ✅ group radios per row
                            value={alpha}
                            checked={selectedArr[0] === alpha}
                            onChange={() => {
                              setMatrixAnswer((prev) => ({
                                ...prev,
                                [rowKey]: [alpha], // ✅ always store as array with ONE value
                              }));
                            }}
                            style={{ marginRight: "30px", cursor: "pointer" }}
                          />
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Box>
        );
      case "Match Matrix":
        return (
          <Box sx={{ pb: 10 }}>
            <div
              className={classes.options}
              style={{
                display: "flex",
                paddingLeft: "55px",
                gap: "50px",
                marginTop: 30,
              }}
            >
              {sortAlphabetArray(x).map((option, index) => (
                <div key={index}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: selectedLanguage === "english" ? option : option,
                    }}
                  />
                </div>
              ))}
            </div>
            <ul className={classes.options}>
              {question.options.map((option, index) => {
                if (
                  (!option.option_en && !option.option_hi) ||
                  (!option.solution_en && !option.solution_hi)
                ) {
                  return;
                }

                return (
                  <li
                    key={index}
                    style={{ display: "list-item", listStyle: "none" }}
                    onClick={(e) => {
                      // Detect if user clicked an image inside HTML
                      if (e.target.tagName === "IMG") {
                        handleOptionImageClick(e);
                      }
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center" }}
                      onClick={(e) => e.preventDefault()}
                    >
                      {" "}
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            selectedLanguage === "english"
                              ? option.option_en
                              : option.option_hi,
                        }}
                      />
                      <div style={{ marginLeft: "20px", marginBottom: "10px" }}></div>
                      {sortAlphabetArray(x).map((alpha) => {
                        const rowKey =
                          selectedLanguage === "english"
                            ? option.option_en
                            : option.option_hi;

                        const selectedArray = matrixAnswer[rowKey] || [];

                        return (
                          <input
                            key={alpha}
                            type="checkbox"
                            checked={selectedArray.includes(alpha)}
                            onChange={() => {
                              setMatrixAnswer((prev) => {
                                const prevArr = prev[rowKey] || [];

                                const newArr = prevArr.includes(alpha)
                                  ? prevArr.filter((v) => v !== alpha) // remove
                                  : [...prevArr, alpha]; // add

                                return {
                                  ...prev,
                                  [rowKey]: newArr, // ✅ STORE ARRAY
                                };
                              });
                            }}
                            style={{ marginRight: "30px", cursor: "pointer" }}
                          />
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <div className={classes.questionsContainerinner}>
      <Typography variant="body1" gutterBottom className={classes.questiontext}>
        <span
          onClick={(e) => {
            // Detect if user clicked an image inside HTML
            if (e.target.tagName === "IMG") {
              handleImageClick(e);
            }
          }}
          style={{
            display: "block",
            lineHeight: 1.6,
            fontSize: "1rem",
          }}
          dangerouslySetInnerHTML={{
            __html:
              selectedLanguage === "english"
                ? question?.question_en
                : question?.question_hi,
          }}
        />
        {/* 
        <style jsx>{`
          span img {
            cursor: zoom-in;
          }
        `}</style> */}
        <style jsx>{`
          span img {
            cursor: zoom-in;
            max-width: 100% !important;
            height: auto !important;
          }
          @media (min-width: 600px) {
            span img {
              max-width: 100% !important;
              height: auto !important;
            }
          }
          @media (max-width: 599px) {
            span img {
              max-width: 85% !important;
              width: auto !important;
            }
          }
          @media (min-width: 800px) {
            span img {
              max-width: 100%;
              width: auto;
            }
          }
        `}</style>
        {/* Image Preview Modal */}
        <Dialog
          open={open}
          onClose={() => {
            setOpen(false);
            handleResetZoom();
            setTimeout(() => {
              setOptionClick(false);
            }, 300);
          }}
          maxWidth={false}
          sx={{
            "& .MuiDialog-paper": {
              width: { xs: "98vw", sm: "90vw", md: "85vw" },
              height: { xs: "70vh", sm: "80vh", md: "85vh" },
              maxHeight: "95vh",
              margin: { xs: "4px", md: "16px" },
              borderRadius: { xs: "12px", md: "8px" },
              // backgroundColor: "#1a1a1a", // Modern dark background for focus
              backgroundColor: "#fff",
              boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
              overflow: "hidden",
            },
          }}
        >
          <IconButton
            onClick={() => {
              setOpen(false);
              handleResetZoom();
              setTimeout(() => {
                setOptionClick(false);
              }, 300);
            }}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 1300,
              // color: "white",
              color: "black",
              // backgroundColor: "rgba(255,255,255,0.1)",
              backgroundColor: "rgba(0,0,0,0.05)",
              backdropFilter: "blur(4px)",
              "&:hover": {
                // backgroundColor: "rgba(255,255,255,0.2)"
                backgroundColor: "rgba(0,0,0,0.1)"
              },
              padding: "8px",
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>

          <DialogContent
            sx={{
              p: 0,
              // backgroundColor: "#111",
              backgroundColor: "#fff",
              overflow: "hidden",
              position: "relative",
              height: "100%",
              width: "100%",
            }}
          >
            <Draggable
              nodeRef={draggableRef}
              disabled={scale === 1}
              position={{ x: 0, y: 0 }}
              onDrag={(e, data) => setPosition(prev => ({ x: prev.x + data.deltaX, y: prev.y + data.deltaY }))}
            >
              <Box
                ref={draggableRef}
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleClick}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: scale > 1 ? "grab" : "zoom-in",
                  "&:active": { cursor: scale > 1 ? "grabbing" : "zoom-in" },
                  transition: scale === 1 ? "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
                  touchAction: scale > 1 ? "none" : "auto", // Disable native scroll when panning
                  width: "100%",
                  height: "100%",
                }}
              >
                <img
                  src={selectedImg}
                  alt="Preview"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transition: scale === 1 ? "transform 0.3s ease" : "none",
                    maxWidth: "90%",
                    maxHeight: "90%",
                    objectFit: "contain",
                    userSelect: "none",
                    pointerEvents: "none", // Prevent native drag but allow Box events
                  }}
                />
              </Box>
            </Draggable>

            {/* Premium Zoom Controls ToolBar */}
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                // backgroundColor: "rgba(255,255,255,0.1)",
                backgroundColor: "rgba(0,0,0,0.8)",
                backdropFilter: "blur(12px)",
                padding: "6px 12px",
                borderRadius: "30px",
                // border: "1px solid rgba(255,255,255,0.1)",
                border: "1px solid rgba(0,0,0,0.1)",
                zIndex: 1200,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              }}
            >
              <IconButton
                onClick={handleZoomOut}
                sx={{ color: "white", "&:disabled": { color: "rgba(255,255,255,0.3)" } }}
                disabled={scale <= 1}
                size="small"
              >
                <ZoomOutIcon />
              </IconButton>

              <Typography
                variant="caption"
                sx={{
                  color: "white",
                  minWidth: "45px",
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px"
                }}
              >
                {Math.round(scale * 100)}%
              </Typography>

              <IconButton
                onClick={handleZoomIn}
                sx={{ color: "white", "&:disabled": { color: "rgba(255,255,255,0.3)" } }}
                disabled={scale >= 4}
                size="small"
              >
                <ZoomInIcon />
              </IconButton>

              <Box sx={{ width: "1px", height: "16px", backgroundColor: "rgba(255,255,255,0.2)", mx: 1 }} />

              <IconButton
                onClick={handleResetZoom}
                sx={{ color: "white" }}
                size="small"
                title="Reset Zoom"
              >
                <RestartAltIcon />
              </IconButton>
            </Box>
          </DialogContent>
        </Dialog>
      </Typography>

      {/* {(question?.description_en || question?.description_hi) && (
        <p style={{ paddingTop: "10px", paddingBottom: "10px" }}>
          Description:{" "}
          <p style={{ paddingTop: "5px", paddingBottom: "5px" }}></p>
          <span
            dangerouslySetInnerHTML={{
              __html:
                selectedLanguage === "english"
                  ? question?.description_en
                  : question?.description_hi,
            }}
          />
        </p>
      )} */}

      {questionType && (
        <>
          {decryptData(sessionStorage.getItem("exam_template")) ===
            "default" && (
              <Typography variant="h5">
                {questionType === "Fill In The Blanks" ? "Answer:" : "Options:"}
              </Typography>
            )}

          {renderOptions()}
        </>
      )}
    </div>
  );
};
